import { useEffect, useState } from 'react';
import { callFirebaseFunction } from '../../../../../../services/firebaseFunctions';

export interface WorkdayApiCredentials {
  clientId: string;
  isuUsername: string;
  privateKey: string;
  tenant: string;
  apiHost?: string;
}

export interface WorkdayApiConnectionStatus {
  connected: boolean;
  credentials: WorkdayApiCredentials | null;
}

/**
 * Save Workday API credentials
 */
export const saveWorkdayApiCredentials = async (
  orgId: string,
  credentials: WorkdayApiCredentials
): Promise<{ success: boolean }> => {
  return callFirebaseFunction('workday_api_saveCredentials', {
    orgId,
    ...credentials
  });
};

/**
 * Test Workday API connection
 */
export const testWorkdayApiConnection = async (
  orgId: string
): Promise<{ success: boolean }> => {
  return callFirebaseFunction('workday_api_testConnection', { orgId });
};

/**
 * Get Workday API connection status
 */
export const getWorkdayApiConnectionStatus = async (
  orgId: string
): Promise<WorkdayApiConnectionStatus> => {
  return callFirebaseFunction('workday_api_getConnectionStatus', { orgId });
};

/**
 * Disconnect Workday API
 */
export const disconnectWorkdayApi = async (
  orgId: string
): Promise<{ success: boolean }> => {
  return callFirebaseFunction('workday_api_disconnect', { orgId });
};

/**
 * Hook to get Workday API connection status
 */
export const useWorkdayApiStatus = (orgId: string) => {
  const [status, setStatus] = useState<WorkdayApiConnectionStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const refresh = async () => {
    setLoading(true);
    try {
      const result = await getWorkdayApiConnectionStatus(orgId);
      setStatus(result);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to get status'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, [orgId]);

  return { status, loading, error, refresh };
};

// =============================================================================
// SOAP API (WS-Security for Pre-hire Updates)
// =============================================================================

export interface WorkdaySoapCredentials {
  isuUsername: string;
  isuPassword: string;
  tenant: string;
  apiHost?: string;
}

export interface WorkdaySoapConnectionStatus {
  connected: boolean;
  credentials: WorkdaySoapCredentials | null;
}

/**
 * Save Workday SOAP API credentials
 */
export const saveWorkdaySoapCredentials = async (
  orgId: string,
  credentials: WorkdaySoapCredentials
): Promise<{ success: boolean }> => {
  return callFirebaseFunction('workday_soap_saveCredentials', {
    orgId,
    ...credentials
  });
};

/**
 * Test Workday SOAP API connection
 */
export const testWorkdaySoapConnection = async (
  orgId: string
): Promise<{ success: boolean }> => {
  return callFirebaseFunction('workday_soap_testConnection', { orgId });
};

/**
 * Get Workday SOAP API connection status
 */
export const getWorkdaySoapConnectionStatus = async (
  orgId: string
): Promise<WorkdaySoapConnectionStatus> => {
  return callFirebaseFunction('workday_soap_getConnectionStatus', { orgId });
};

/**
 * Disconnect Workday SOAP API
 */
export const disconnectWorkdaySoap = async (
  orgId: string
): Promise<{ success: boolean }> => {
  return callFirebaseFunction('workday_soap_disconnect', { orgId });
};

/**
 * Hook to get Workday SOAP API connection status
 */
export const useWorkdaySoapStatus = (orgId: string) => {
  const [status, setStatus] = useState<WorkdaySoapConnectionStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const refresh = async () => {
    setLoading(true);
    try {
      const result = await getWorkdaySoapConnectionStatus(orgId);
      setStatus(result);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to get SOAP status'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, [orgId]);

  return { status, loading, error, refresh };
};
